/**
 * GalleryJobPage - Job details and Jobber enrichment data.
 *
 * Displays comprehensive job information from Jobber CRM:
 * - Client information
 * - Service location
 * - Job metadata (status, total, number)
 * - Line items table
 * - Visits timeline
 * - Invoice summary
 *
 * Wraps the existing GalleryEnrichmentPanel with enhanced layout.
 *
 * @see /src/features/gallery/layouts/GalleryLayout.tsx - Parent layout
 * @see /src/features/gallery/components/detail/GalleryEnrichmentPanel.tsx - Core component
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  User,
  MapPin,
  Briefcase,
  FileText,
  DollarSign,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Sparkles,
  Loader2,
  CheckCircle,
  Clock,
  Receipt,
  Phone,
  Mail,
  Home,
  Database,
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useNavigate } from 'react-router-dom'

import { useGalleryDetail } from '../context/GalleryDetailContext'
import GalleryEnrichmentPanel from '../components/detail/GalleryEnrichmentPanel'
import { useCreateProjectFromGallery } from '../hooks/useCreateProjectFromGallery'
import { ProjectWriterRunDialog } from '../components/dialogs/ProjectWriterRunDialog'
import { useProjectPageByGallery } from '@/features/project-pages/hooks/useProjectPages'

export default function GalleryJobPage() {
  const {
    gallery,
    enrichment,
    jobDetails,
    jobLinkage,
    jobImages,
    isLoading,
    isJobDetailsLoading,
    syncImages,
    isSyncing,
  } = useGalleryDetail()

  const { toast } = useToast()
  const navigate = useNavigate()
  const [showRunDialog, setShowRunDialog] = useState(false)

  // Query for existing project page linked to this gallery
  const {
    data: existingProjectPage,
    isLoading: isProjectPageLoading
  } = useProjectPageByGallery(gallery?.id)

  // Project page generation
  const {
    generate: generateProjectPage,
    isLoading: isGenerating,
    progress: generationProgress,
    status: generationStatus,
    agentId,
    requestId,
    error: generationError,
  } = useCreateProjectFromGallery({
    onStart: () => setShowRunDialog(true),
    onSuccess: (result) => {
      toast({
        title: 'Project Page Created!',
        description: result.verification_passed
          ? `Quality: ${result.quality_score}/100 ✓ Verified • Preview: ${result.preview_url}`
          : `Quality: ${result.quality_score}/100 - Review recommended • Preview: ${result.preview_url}`,
        variant: 'default',
      })
      // Navigate to edit the new project page
      if (result.project_page_id) {
        navigate(`/content/project-pages/${result.project_page_id}/edit`)
      }
    },
    onError: (error) => {
      toast({
        title: 'Generation Failed',
        description: error,
        variant: 'destructive',
      })
    },
  })

  // Check if gallery is ready for project page generation
  const canGenerateProjectPage = !!(
    enrichment?.enrichment_status === 'enriched' &&
    enrichment?.service_city
  )

  // Build Jobber profile URL
  const jobberUrl = enrichment?.jobber_web_uri
    || jobImages[0]?.jobber_web_uri
    || (enrichment?.job_id
      ? `https://secure.getjobber.com/work_orders/${enrichment.job_id}`
      : null)

  // Check if we have job linkage
  const hasJobLink = !!(jobLinkage?.job_id || enrichment?.job_id)

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-6xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    )
  }

  // No job linked state
  if (!hasJobLink) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-up">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">No Job Linked</h2>
          <p className="text-muted-foreground max-w-md mb-6">
            This gallery doesn't have any job images linked from Jobber.
            Add job images to enable enrichment and sync features.
          </p>
          <Button variant="outline" disabled>
            <Briefcase className="h-4 w-4 mr-2" />
            Link to Jobber Job
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <ProjectWriterRunDialog
        open={showRunDialog}
        onOpenChange={setShowRunDialog}
        agentId={agentId}
        requestId={requestId}
        status={generationStatus}
        isRunning={isGenerating}
        error={generationError}
      />
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-down">
        <div>
          <h2 className="text-xl font-semibold">Job Details</h2>
          <p className="text-sm text-muted-foreground">
            Jobber CRM data for this gallery
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Project Page Actions - Conditional based on existence */}
          {isProjectPageLoading ? (
            // Loading skeleton for button while checking for project page
            <Skeleton className="h-9 w-36" />
          ) : existingProjectPage ? (
            // Project page EXISTS - Show View + Regenerate options
            <>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate(`/content/project-pages/${existingProjectPage.id}/edit`)}
              >
                <FileText className="h-4 w-4 mr-2" />
                View Project Page
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!gallery?.id) return
                  generateProjectPage(gallery.id)
                }}
                disabled={isGenerating || !canGenerateProjectPage}
                title="Regenerate project page with latest gallery data"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {generationProgress}%
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate
                  </>
                )}
              </Button>
            </>
          ) : (
            // Project page DOES NOT exist - Show Create button
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                if (!gallery?.id) return
                generateProjectPage(gallery.id)
              }}
              disabled={isGenerating || !canGenerateProjectPage}
              title={
                !canGenerateProjectPage
                  ? 'Enrich gallery first to enable project page generation'
                  : 'Generate AI-powered project page'
              }
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating... {generationProgress}%
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Create Project Page
                </>
              )}
            </Button>
          )}
          {jobberUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={jobberUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in Jobber
              </a>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={syncImages}
            disabled={isSyncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            Sync Images
          </Button>
        </div>
      </div>

      {/* Quick Stats - uses CSS stagger-children for sequential entrance */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Briefcase className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Job Number</p>
                <p className="text-lg font-semibold">
                  #{jobDetails?.job_number || enrichment?.job_number || gallery?.job_number || '—'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Job Total</p>
                <p className="text-lg font-semibold">
                  {(jobDetails?.total_amount ?? enrichment?.job_total)
                    ? `$${Number(jobDetails?.total_amount ?? enrichment?.job_total).toLocaleString()}`
                    : '—'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <Badge variant="secondary" className="mt-1 capitalize">
                  {jobDetails?.job_status || enrichment?.job_status || 'Unknown'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <CheckCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="text-lg font-semibold">
                  {jobDetails?.completed_at
                    ? new Date(jobDetails.completed_at).toLocaleDateString()
                    : enrichment?.service_date
                      ? new Date(enrichment.service_date).toLocaleDateString()
                      : '—'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary */}
      {(jobDetails?.invoiced_total !== null || jobDetails?.uninvoiced_total !== null) && (
        <div className="grid gap-4 sm:grid-cols-3 animate-fade-up">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Receipt className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Invoiced</p>
                  <p className="text-lg font-semibold text-green-600">
                    ${Number(jobDetails?.invoiced_total ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Uninvoiced</p>
                  <p className="text-lg font-semibold text-orange-600">
                    ${Number(jobDetails?.uninvoiced_total ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Line Items</p>
                  <p className="text-lg font-semibold">
                    {jobDetails?.line_items?.length ?? enrichment?.line_items?.length ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client & Property Info */}
      {(jobDetails?.client_data || jobDetails?.property_data) && (
        <div className="grid gap-4 md:grid-cols-2 animate-fade-up">
          {/* Client Info */}
          {jobDetails?.client_data && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Client Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="font-medium">
                    {jobDetails.client_data.firstName} {jobDetails.client_data.lastName}
                  </p>
                </div>
                {jobDetails.client_data.emails?.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <a
                      href={`mailto:${jobDetails.client_data.emails[0]}`}
                      className="hover:text-foreground hover:underline"
                    >
                      {jobDetails.client_data.emails[0]}
                    </a>
                  </div>
                )}
                {jobDetails.client_data.phones?.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <a
                      href={`tel:${jobDetails.client_data.phones[0]}`}
                      className="hover:text-foreground hover:underline"
                    >
                      {jobDetails.client_data.phones[0]}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Property Info */}
          {jobDetails?.property_data && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Service Location
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="text-sm">
                    <p>{jobDetails.property_data.street1}</p>
                    {jobDetails.property_data.street2 && (
                      <p>{jobDetails.property_data.street2}</p>
                    )}
                    <p>
                      {jobDetails.property_data.city}, {jobDetails.property_data.province} {jobDetails.property_data.postalCode}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Line Items Table - Prefer enrichment data which has correct pricing */}
      {(() => {
        // Prefer enrichment line_items (has unitCost with actual prices) over jobDetails (has unitCost = 0)
        const lineItemsSource = enrichment?.line_items?.length ? enrichment.line_items : jobDetails?.line_items
        const lineItems = Array.isArray(lineItemsSource) ? lineItemsSource : []

        if (lineItems.length === 0) return null

        return (
          <Card className="animate-fade-up">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">Service</th>
                      <th className="text-right py-2 font-medium">Qty</th>
                      <th className="text-right py-2 font-medium">Price</th>
                      <th className="text-right py-2 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item: any, index: number) => {
                      // Handle both camelCase and snake_case field names from API
                      const unitPrice = item.unitPrice ?? item.unit_price ?? item.unitCost ?? item.unit_cost ?? 0
                      const lineTotal = item.total ?? (item.quantity * unitPrice)

                      return (
                        <tr key={item.id || index} className="border-b last:border-0">
                          <td className="py-3">
                            <p className="font-medium">{item.name}</p>
                            {item.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {item.description}
                              </p>
                            )}
                          </td>
                          <td className="text-right py-3 align-top tabular-nums">
                            {item.quantity}
                          </td>
                          <td className="text-right py-3 align-top tabular-nums">
                            ${Number(unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="text-right py-3 align-top font-medium tabular-nums">
                            ${Number(lineTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2">
                      <td colSpan={3} className="py-3 font-semibold text-right">
                        Total
                      </td>
                      <td className="py-3 text-right font-bold text-lg tabular-nums">
                        ${Number(jobDetails?.total_amount ?? enrichment?.job_total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )
      })()}

      {/* Main Enrichment Panel */}
      <div className="animate-fade-up animate-delay-200">
        <GalleryEnrichmentPanel
          galleryId={gallery?.id ?? 0}
          jobNumber={gallery?.job_number ?? undefined}
          franchise={gallery?.franchise_region ?? undefined}
          jobberProfileUrl={jobberUrl ?? undefined}
        />
      </div>

      {/* Job Linkage Info */}
      <div className="animate-fade-in animate-delay-300">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Job Linkage
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Job ID</span>
              <code className="text-xs bg-muted px-2 py-0.5 rounded">
                {jobLinkage?.job_id || enrichment?.job_id || '—'}
              </code>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Franchise Region</span>
              <span className="capitalize">
                {(jobLinkage?.franchise_region || enrichment?.franchise_region || '—').replace('_', ' ')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Enrichment Status</span>
              <Badge
                variant={
                  enrichment?.enrichment_status === 'enriched'
                    ? 'success'
                    : enrichment?.enrichment_status === 'failed'
                      ? 'destructive'
                      : 'secondary'
                }
              >
                {enrichment?.enrichment_status || 'Not enriched'}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground flex items-center gap-1.5">
                <Database className="h-3.5 w-3.5" />
                Job Details
              </span>
              {isJobDetailsLoading ? (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Checking...
                </span>
              ) : jobDetails ? (
                <Badge variant="success" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Synced
                </Badge>
              ) : (
                <Badge variant="outline" className="text-orange-600 border-orange-300 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Not synced
                </Badge>
              )}
            </div>
            {jobDetails?.last_fetched_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Details Last Synced</span>
                <span className="text-xs">{new Date(jobDetails.last_fetched_at).toLocaleString()}</span>
              </div>
            )}
            {enrichment?.fetched_on && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Enrichment Fetched</span>
                <span className="text-xs">{new Date(enrichment.fetched_on).toLocaleString()}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
